import { Component, OnInit, ViewChild, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { LoadingBoxComponent } from '@shared/components/loading-box/loading-box.component';
import { TimelineHistoricoComponent, TimelineHistoricoItem } from '@shared/components/timeline-historico/timeline-historico.component';
import { OrigemOcorrenciaEnum } from '@shared/enums/repo-dfe/origem-ocorrencia.enum';
import { PermissaoEnum } from '@shared/enums/usuario/permissao.enum';
import { classeBadgeStatusNFeSaida } from '@shared/helpers/nota-fiscal-saida-status.helper';
import { AlertService, AlertType } from '@shared/services/alert.service';
import { NotaFiscalSaidaService } from '@shared/services/apis/orquestrador-vendas/nota-fiscal-saida.service';
import { AuthService } from '@shared/services/apis/usuario/auth.service';
import { CriarNotaFiscalSaidaOcorrenciaRequest } from '@shared/services/requests/orquestrador-vendas/criar-nota-fiscal-saida-ocorrencia.request';
import { NFeSaidaOcorrenciaStruct } from '@shared/services/structs/repo-dfe/nfe-saida-ocorrencia.struct';
import { dataParaDiaCivil, obterDataAtualDaEmpresa, obterInicioDoDiaDaEmpresa } from '@shared/helpers/datetime.util';

export interface NfeHistoricoDialogData {
  idNFeSaida: number;
  titulo: string;
}

/**
 * "Ver histórico" da NF-e de saída: as ocorrências da nota em `app-timeline-historico`, com filtros e,
 * para quem pode alterar a nota, a anotação manual.
 */
@Component({
  selector: 'app-nfe-historico-dialog',
  standalone: true,
  imports: [MatDialogTitle, MatDialogContent, MatDialogActions, MatButtonModule, MatIconModule, MatProgressSpinnerModule, LoadingBoxComponent, TimelineHistoricoComponent],
  templateUrl: './nfe-historico-dialog.component.html',
  styleUrls: ['./nfe-historico-dialog.component.css'],
})
export class NfeHistoricoDialogComponent implements OnInit {
  private readonly dialogRef = inject(MatDialogRef<NfeHistoricoDialogComponent>);
  private readonly notaFiscalSaidaService = inject(NotaFiscalSaidaService);
  private readonly authService = inject(AuthService);
  private readonly alertService = inject(AlertService);
  readonly data = inject<NfeHistoricoDialogData>(MAT_DIALOG_DATA);

  @ViewChild(TimelineHistoricoComponent) timeline?: TimelineHistoricoComponent;

  readonly podeAnotar = (this.authService.getUserInfos()?.listaPermissoes ?? []).includes(
    PermissaoEnum.Vendas_NotasFiscais_Alterar
  );
  isLoading = true;
  isRegistrandoAnotacao = false;
  itens: TimelineHistoricoItem[] = [];

  ngOnInit(): void {
    this.carregar();
  }

  /** O skeleton é só da primeira carga; depois de anotar, a lista troca no lugar. */
  private carregar(): void {
    this.notaFiscalSaidaService.getHistorico(this.data.idNFeSaida).subscribe({
      next: res => {
        this.isLoading = false;
        if (res.isError) {
          this.alertService.show('Aviso', res.errorDescription, AlertType.warning);
          return;
        }
        this.itens = (res.listaOcorrencia ?? []).map(ocorrencia => this.paraItem(ocorrencia));
      },
      error: () => {
        this.isLoading = false;
        this.alertService.show('Erro', 'Não foi possível carregar o histórico.', AlertType.error);
      },
    });
  }

  /** Sem usuário, o autor é a origem; a origem só aparece como badge quando não é o próprio usuário. */
  private paraItem(ocorrencia: NFeSaidaOcorrenciaStruct): TimelineHistoricoItem {
    return {
      icone: ocorrencia.nomeIconeTipoOcorrencia,
      titulo: ocorrencia.nomeTipoOcorrencia,
      descricao: ocorrencia.descricao,
      detalhe: ocorrencia.detalhes,
      justificativa: ocorrencia.justificativa,
      dados: ocorrencia.dados,
      quando: ocorrencia.dataOcorrencia,
      quemNome: ocorrencia.nomeUsuario || ocorrencia.nomeOrigemOcorrencia,
      nomeOrigem: ocorrencia.nomeOrigemOcorrencia,
      iconeOrigem: ocorrencia.nomeIconeOrigemOcorrencia,
      exibirOrigem: ocorrencia.idOrigemOcorrencia !== OrigemOcorrenciaEnum.Usuario,
      nomeStatusAnterior: ocorrencia.nomeStatusNFeSaidaAnterior,
      classeStatusAnterior:
        ocorrencia.idStatusNFeSaidaAnterior != null ? classeBadgeStatusNFeSaida(ocorrencia.idStatusNFeSaidaAnterior) : null,
      nomeStatusAtual: ocorrencia.nomeStatusNFeSaidaAtual,
      classeStatusAtual:
        ocorrencia.idStatusNFeSaidaAtual != null ? classeBadgeStatusNFeSaida(ocorrencia.idStatusNFeSaidaAtual) : null,
    };
  }

  /** Anotação do dia vai sem data, para o servidor gravar a hora do registro; retroativa leva o dia escolhido. */
  registrarAnotacao(anotacao: { descricao: string; dataOcorrencia: Date }): void {
    const request = new CriarNotaFiscalSaidaOcorrenciaRequest();
    request.descricao = anotacao.descricao;
    const timezone = this.authService.getUserInfos().timezone;
    request.dataOcorrencia = dataParaDiaCivil(anotacao.dataOcorrencia) === dataParaDiaCivil(obterDataAtualDaEmpresa(timezone))
      ? null
      : obterInicioDoDiaDaEmpresa(anotacao.dataOcorrencia, timezone);

    this.isRegistrandoAnotacao = true;
    this.notaFiscalSaidaService.criarOcorrencia(this.data.idNFeSaida, request).subscribe({
      next: res => {
        this.isRegistrandoAnotacao = false;
        if (res.isError) {
          this.alertService.show('Aviso', res.errorDescription, AlertType.warning);
          return;
        }
        this.timeline?.limparAnotacao();
        this.alertService.show('Sucesso', 'Anotação registrada.', AlertType.success);
        this.carregar();
      },
      error: () => {
        this.isRegistrandoAnotacao = false;
        this.alertService.show('Erro', 'Não foi possível registrar a anotação.', AlertType.error);
      },
    });
  }

  fechar(): void {
    this.dialogRef.close();
  }
}
